import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Mail, MailCheck, Eye, EyeOff, RefreshCw, Users, TrendingDown,
  ArrowDownRight, ArrowUpRight, Minus, Loader2, AlertCircle,
} from "lucide-react";

/**
 * CareDigestPreview — Monday-morning digest rehearsal for the treating
 * specialist. Shows exactly which patients will appear in the auto-send
 * ranked by biggest MHI/NHI/CHI dip over the last 15 days, with a
 * "Send me a test copy now" button that bypasses the ISO-week guard.
 *
 * Wired to two existing endpoints:
 *   GET  /api/specialist/care-digest/preview   → { ready, payload | reason }
 *   POST /api/specialist/care-digest/send-now  → { sent, reason? }
 */

const BAND_STYLE = {
  excellent:  { bg: "#e6f8ec", ink: "#217a3d", label: "Excellent" },
  good:       { bg: "#e9f2fb", ink: "#1360a8", label: "Good" },
  fair:       { bg: "#fff5e5", ink: "#b46a13", label: "Fair" },
  attention:  { bg: "#fdecec", ink: "#a1272b", label: "Needs attention" },
  unassessed: { bg: "#f2f3f5", ink: "#5f6672", label: "New" },
};

function DeltaChip({ delta }) {
  if (delta === null || delta === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
        <Minus size={10} /> —
      </span>
    );
  }
  const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const meta = {
    up:   { Icon: ArrowUpRight,   color: "#217a3d", bg: "#e6f8ec" },
    down: { Icon: ArrowDownRight, color: "#a1272b", bg: "#fdecec" },
    flat: { Icon: Minus,          color: "#5f6672", bg: "#f2f3f5" },
  }[dir];
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ background: meta.bg, color: meta.color }}
    >
      <meta.Icon size={10} />
      {delta > 0 ? "+" : ""}{delta}
    </span>
  );
}

function PatientRow({ row }) {
  const dip = row.biggest_dip || 0;
  const isDip = dip >= 10;
  return (
    <div
      data-testid={`care-digest-row-${row.patient_id}`}
      className={`rounded-2xl border p-4 ${
        isDip
          ? "border-red-200 bg-red-50/40"
          : "border-[color:var(--mano-border)] bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-[color:var(--mano-ink)]">
            {row.patient_name}
          </div>
          <div className="text-[11px] text-[color:var(--mano-ink-soft)] mt-0.5">
            {row.patient_email}
          </div>
        </div>
        {isDip && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-800">
            <TrendingDown size={10} /> −{dip} pts
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {row.indices.map((idx) => {
          const style = BAND_STYLE[idx.band] || BAND_STYLE.unassessed;
          return (
            <div
              key={idx.code}
              className="rounded-xl border border-[color:var(--mano-border)] bg-white p-2.5 flex flex-col items-start gap-1"
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--mano-ink-soft)]">
                  {idx.code}
                </span>
                <DeltaChip delta={idx.delta} />
              </div>
              <div className="font-display text-lg leading-none" style={{ color: style.ink }}>
                {idx.score}
                <span className="text-[10px] font-semibold text-[color:var(--mano-ink-soft)] ml-0.5">/100</span>
              </div>
              <span
                className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ background: style.bg, color: style.ink }}
              >
                {idx.placeholder ? "Preview" : style.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CareDigestPreview() {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [state, setState] = useState({ ready: null, payload: null, reason: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/specialist/care-digest/preview");
      setState(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/specialist/care-digest/send-now");
      if (data.sent) {
        toast.success("Digest sent to your inbox — check your email.");
      } else {
        toast.info(
          data.reason === "no_recent_patients"
            ? "No patients seen in the last 60 days — nothing to send yet."
            : "Digest not sent — try again once you have recent consults."
        );
      }
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSending(false);
    }
  };

  const payload = state.payload;
  const ready = state.ready;
  const rows = payload?.rows || [];
  const shown = expanded ? rows : rows.slice(0, 3);

  return (
    <section
      data-testid="care-digest-preview"
      className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6 lg:p-7 space-y-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="uppercase-label inline-flex items-center gap-1">
            <MailCheck size={14} /> Monday care digest · preview
          </div>
          <h2 className="font-display text-xl mt-1 leading-snug">
            Rehearse before it lands in your inbox
          </h2>
          <p className="text-sm text-[color:var(--mano-ink-soft)] mt-1 max-w-2xl">
            Every Monday morning we email you a ranked list of patients you've
            seen recently, sorted by biggest 15-day dip in MHI / NHI / CHI. This
            preview shows exactly what the next send will contain.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="care-digest-refresh"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-[color:var(--mano-border)] bg-white hover:bg-[color:var(--mano-mild)]/50 disabled:opacity-50"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <button
            data-testid="care-digest-send-now"
            onClick={sendNow}
            disabled={sending || !ready}
            title={!ready ? "Nothing to send yet" : "Send a live copy to your email now"}
            className="inline-flex items-center gap-1 text-xs font-semibold px-4 py-1.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white transition-colors disabled:opacity-60"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
            {sending ? "Sending…" : "Email me a test copy"}
          </button>
        </div>
      </div>

      {!ready && state.reason === "no_recent_patients" && (
        <div
          data-testid="care-digest-empty"
          className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-6 text-center text-sm text-[color:var(--mano-ink-soft)]"
        >
          <Users size={20} className="mx-auto text-[color:var(--mano-ink-soft)] mb-2" />
          No recent consults — the digest kicks in as soon as you see your first
          patient in the last 60 days.
        </div>
      )}

      {!ready && state.reason === "no_expert_record" && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          Your specialist profile isn't linked yet. Ask an admin to approve your
          application before the digest will populate.
        </div>
      )}

      {ready && payload && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <MetricTile
              value={payload.patient_count}
              label="Patients"
              testid="care-digest-metric-patients"
            />
            <MetricTile
              value={payload.dip_count}
              label="Big dips (≥ 10 pts)"
              testid="care-digest-metric-dips"
              tone={payload.dip_count > 0 ? "warn" : "ok"}
            />
            <MetricTile
              value={payload.lookback_days}
              label="Days looked back"
              testid="care-digest-metric-lookback"
            />
          </div>

          <div className="space-y-2.5" data-testid="care-digest-rows">
            {shown.map((row) => (
              <PatientRow key={row.patient_id} row={row} />
            ))}
          </div>

          {rows.length > 3 && (
            <button
              data-testid="care-digest-toggle"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--mano-primary)]"
            >
              {expanded ? (
                <><EyeOff size={12} /> Show top 3</>
              ) : (
                <><Eye size={12} /> Show all {rows.length} patients</>
              )}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function MetricTile({ value, label, testid, tone = "neutral" }) {
  const tint = tone === "warn"
    ? "bg-red-50 text-red-800 border-red-100"
    : tone === "ok"
    ? "bg-emerald-50 text-emerald-800 border-emerald-100"
    : "bg-[color:var(--mano-bg)] text-[color:var(--mano-ink)] border-[color:var(--mano-border)]";
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-3 ${tint}`}
    >
      <div className="font-display text-2xl leading-none">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70 mt-1.5">
        {label}
      </div>
    </div>
  );
}
